<!-- 2026 Registration page after April's event: now for VIZBI Online (November 2026), in USD.
     First stab 2026-10-07. Text that no longer applies (the April recordings-only offer) is kept
     in comments rather than deleted. -->

<template>
  <!-- the calculator didn't show the combined offer; replaced by #register's rates (2026-10-07)
  <Calculator
    :options="{events, discounts: onlineDiscounts, abbreviations, currency, isEarlyBirdPast}" />
  -->
  <RegionalRates :events="events" :currency="currency" />
  <RegisterNowWrapper />

  <!-- "What's available" and "Future online events" are merged into "VIZBI Online" below
  <h4>What's available</h4>
  <p>
    VIZBI 2026 has now concluded. You can still register for the
    <b><i>Virtual Masterclass</i></b> or <b><i>Virtual Workshop</i></b
    >, which give you immediate on-demand access to all 2026 talk recordings, as well as the full
    archive from previous VIZBI meetings.
  </p>

  <h4>Future online events</h4>
  <p>
    We may organise additional online sessions with 2026 speakers later this year, where you can
    engage directly in a live Q&amp;A format. If there is sufficient demand, these sessions will be
    scheduled and all registered participants will have access.
  </p>
  -->

  <h4>VIZBI <i>Online</i></h4>
  <p>
    This November, we hold live online sessions with the 2026 speakers: the
    <b><i>Masterclass</i></b> ({{ masterclass }}{{ noteInParens }}) and the <b><i>Workshop</i></b>
    ({{ workshop }}{{ noteInParens }}), {{ hours }} each day. These sessions build on the talks
    recorded at VIZBI 2026 in April, and registering unlocks those recordings instantly, so you can
    <router-link to="/2026#faq&prepare">watch them before the live sessions</router-link>. If you
    attended the Masterclass or Workshop in April, the
    <router-link to="/2026#faq&cambridge">matching online event is free</router-link>.
  </p>

  <RegisterNowWrapper is-always-shown class="isFullWidth" />

  <h4>What's included</h4>
  <ul>
    <!-- April's recordings-only offer
    <li>
      <b><i>VIZBI Virtual Workshop</i></b> gives on-demand access to all plenary talks and keynotes
      from VIZBI 2026, plus the complete archive of recordings from previous VIZBI workshops.
    </li>
    <li>
      <b><i>VIZBI Virtual Masterclass</i></b> gives on-demand access to the full one-day Masterclass
      from VIZBI 2026, covering data visualization principles including design, typography,
      storytelling, and AI-assisted methods, plus over 30 hours of additional on-demand training
      from previous Masterclasses.
    </li>
    -->
    <li>
      <b><i>Masterclass</i></b>: the live online session on {{ masterclass }}{{ noteAfter }}, and
      immediate and ongoing access to
      <RouterLink to="/Videos/Masterclass">{{ masterclassVideos }}</RouterLink> of past
      Masterclasses, including this year's, on visual design, AI-assisted data visualization,
      animated data storytelling and Blender programming.
    </li>
    <li>
      <b><i>Workshop</i></b>: the live online sessions on {{ workshop }}{{ noteAfter }}, and
      on-demand access to all VIZBI Workshop videos: the plenary talks and keynotes from VIZBI 2026,
      plus the complete archive of recordings from previous VIZBI workshops.
    </li>
    <li>
      <b><i>Both events</i></b>: registering for both together saves $50 (adjusted to your
      region's rate).
    </li>
    <li>
      <!-- as the 2026 FAQ (Seán, 2026-10-22, issue R2) -->
      <b><i>Attendance certificates</i></b>: every attendee receives one by email after the
      event, hosted online with a shareable link and a PDF.
    </li>
  </ul>

  <!-- the same deal and flow as #register's 2-for-1 tickbox and its "?" (fullstack Rates.vue),
       expanded -->
  <!-- stable anchor: /2026/Registration#two-for-one (the 2026 page links here) -->
  <h4 id="two-for-one">2-for-1: Register a colleague for free</h4>
  <p>
    The <b><i>2-for-1 deal</i></b> lets you and your colleague register for the price of one,
    giving both of you ongoing access to the videos included with your registration. You will both
    be registered for the same event(s) with the same registration type. The Early-bird rate still
    applies, but 2-for-1 can't be combined with other discounts (ISCB, DVS or other discount
    codes).
  </p>
  <p>
    To use it, tick <b><i>2-for-1</i></b> when you register, then enter your colleague's email in
    the next step. Once you've paid, we'll email your colleague a link to complete their
    registration by providing their own details and consent.
  </p>
  <!-- who pays (Seán, 2026-10-22): one of the two, for both; a split invoice on request -->
  <p>
    One of you pays for both registrations. If you'd like the cost split between you, a split
    invoice is available on request (<Email to="vizbi-registration" />).
  </p>

  <h4>Discounted registration</h4>
  <ul>
    <li v-if="isEarlyBird">
      <b><i>Early-bird:</i></b> {{ earlyBirdPercent }}% off when you register by
      {{ earlyBirdEnd }}.
    </li>
    <li>
      <b><i>ISCB members</i></b> receive a 15% discount on registration. For these and
      <a href="http://www.iscb.org/iscb-membership">other benefits</a>, consider joining the ISCB
      (<a href="http://iscb.org/">International Society for Computational Biology</a>).
    </li>
    <li>
      <b><i>DVS discount:</i></b> General Members of the
      <a href="https://www.datavisualizationsociety.org/mission">Data Visualization Society</a>
      (DVS) receive a <b>15% discount</b> on registration. To take advantage of this discount,
      follow the instructions on the
      <a href="https://www.datavisualizationsociety.org/discount-codes"
      >DVS partners discount page</a
      >
      (only accessible to general members).
    </li>
    <!-- moved to "Regional Rates" below: regional pricing isn't a discount
    <li>
      <b><i>Regional discounts</i></b> are provided for participants based in lower income
      countries. Please contact us to discuss eligibility (<Email to="vizbi-registration" />).
    </li>
    -->
  </ul>

  <h4>Regional Rates</h4>
  <p>
    Registration fees depend on where you're based: each region has its own rate, set by the
    country's income group. The fees above are for high-income countries; when you register, you
    see the rate for your region. If your rate doesn't match where you're based, please contact us
    (<Email to="vizbi-registration" />).
  </p>

  <!-- how payment works, and registering for someone else (Seán, 2026-10-22, issue R1): what
       #register does (Pay: card, or bank transfer against an invoice; "Someone Else") -->
  <h4>Payment process</h4>
  <p>
    When you register, you're taken to Stripe's secure payment page, on an account managed by the
    <a :href="`${iscbUrl}/who-we-are`">International Society for Computational Biology</a>
    (ISCB). There you pay by credit card, or get the details to pay by bank transfer against an
    invoice (with a $20 processing fee). Your receipt or invoice comes by email from Stripe.
    Fees are in US dollars. All VIZBI finances, including registration payment handling, are
    managed by ISCB, a non-profit entity incorporated in the USA. Their address, phone number,
    and other details are provided on the
    <a :href="`${iscbUrl}/${iscbContactDetails}`">ISCB contact page</a>. If you require additional
    information about ISCB, please contact ISCB support directly via <Email to="iscb-admin" />.
  </p>
  <p>
    Registration fees are generally non-refundable, but if you can't attend in November, you can
    join the next VIZBI <i>Online</i> event instead.
    <router-link to="/2026#faq&cancel">(See FAQ)</router-link>
  </p>

  <h4 id="someone-else">Registering for someone else</h4>
  <p>
    A lab manager, assistant or colleague can register and pay for someone else: when you
    register, choose <b><i>Someone Else</i></b> and give their email. We'll email them a link to
    complete their registration, and the videos are theirs, not yours. With the 2-for-1 deal, you
    can register two colleagues this way.
  </p>
</template>

<script setup>
// with the calculator (commented out above):
import {computed} from "vue";
// import {DateTime} from "luxon";
// import Calculator from "@/components/Registration/Calculator";
import RegionalRates from "@/components/Registration/RegionalRates.vue";
import RegisterNowWrapper from "@/components/CallToAction/RegisterNowWrapper.vue";
import Email from "@/components/Email.vue";
import useEarlyBird from "@/composables/useEarlyBird";
import useLibraryTotals from "@/composables/useLibraryTotals";
import useLocalEventDates from "@/composables/useLocalEventDates";
// import events from "@/assets/years/2026/Registration/events-post-event.json"; // April, GBP
import events from "@/assets/years/2026/Registration/events-online.json"; // November, USD
// import abbreviations from "@/assets/years/2026/Registration/abbreviations.json";
// import {useDiscounts} from "@/composables/useDiscounts.js";
// const discounts = useDiscounts();
const currency = "$"; // VIZBI Online is priced in USD (history.json's "£" stays for April's pages)
// the online events' early-bird deadline (fullstack public/2026/keyDates.json: 30 October)
// const EARLY_BIRD_END = DateTime.fromISO("2026-10-19", {zone: "Europe/London"}).endOf("day");
// const isEarlyBirdPast = computed(() => DateTime.now() > EARLY_BIRD_END);
// the Regional Rate is the price for a region, not a discount: not offered as one here
// const onlineDiscounts = computed(() => {
//   const entries = Object.entries(discounts.value || {});
//   return Object.fromEntries(entries.filter(([key]) => !/regional/i.test(key)));
// });
// the deadline: the fullstack's keyDates.json (earlyBirdDates.js); the line goes once it's over
const {isEarlyBird, endText: earlyBirdEnd, percent: earlyBirdPercent} = useEarlyBird();
const {masterclass, workshop, hours, localNote} = useLocalEventDates();
// "(4 November, Sydney local time)", "4 November (Sydney local time)": only when the viewer's dates
// differ from the program's
const noteInParens = computed(() => (localNote.value ? `, ${localNote.value}` : ""));
const noteAfter = computed(() => (localNote.value ? ` (${localNote.value})` : ""));
// the Masterclass videos' hours (stats.json, 2026 included; issue V2): none until it loads
const {stats} = useLibraryTotals();
// "21 videos (28 hours)" (the Masterclass language guide's form), or "videos" until stats.json
const masterclassVideos = computed(() => {
  const {videos, hours} = stats.value?.masterclass || {};
  return videos && hours ? `${videos} videos (${hours} hours)` : "videos";
});
const iscbUrl = "https://www.iscb.org";
const iscbContactDetails = "49-ISCB%20About%20Us/ISCB%20About%20Us%20Contact%20Us";
</script>

<style scoped>
h4 {
  margin-bottom: 0px;
}
ul {
  list-style-position: outside;
  margin-left: 1rem;
  padding-left: 0;
}
li {
  text-indent: 0;
  line-height: 1.4;
  margin-bottom: 0.6rem;
}
/* the Register Now above "What's included" fills the column's width */
.isFullWidth :deep(a) {
  display: block;
  box-sizing: border-box;
  width: 100%;
}
</style>
